import React, {useCallback, useState} from 'react'
import {useMutation} from '@apollo/client'
import {take} from 'lodash'
import {RouteComponentProps, navigate, Link} from '@reach/router'
import {Formik, FormikProps, FormikConfig, FormikHelpers} from 'formik'

import {Page, Container, Loader} from '../layout'
import {useAuth} from '../../contexts/UserContext'
import {Field} from '../form/fields'
import {accountUpdateMutation} from './queries'
import {
  Gear,
  ArrowRight,
  Building,
  DoorClosed,
} from 'react-bootstrap-icons'
import {AccountUpdate, AccountUpdateVariables} from './_gql-types/AccountUpdate'
import {useApi, fetcher} from '../../api/rest'
import OrderItem from '../Orders/OrderItem'
import AddressItem from '../common/AddressItem'
import RequestItem from '../common/RequestItem'

interface FormValues {
  name: string
}

interface FormErrors {
  name?: string
}

export default function UserSettings (_: RouteComponentProps) {
  const [accountUpdate] = useMutation<AccountUpdate, AccountUpdateVariables>(accountUpdateMutation)
  const [showSuccessAlert, setShowSuccessAlert] = useState<boolean>(false)
  const {user, userLoading, setAuthToken, company} = useAuth()

  const {
    data: ordersData,
    isValidating: ordersIsLoading,
  } = useApi(`/orders/?company=${company?.id}`, fetcher.get)

  const {
    data: requestsData,
    isValidating: requestsIsLoading,
  } = useApi('/company-user-requests/', fetcher.get)

  function onClickLogOut() {
    localStorage.removeItem('companyId')
    setAuthToken()
    navigate('/innskraning/')
  }

  // function validate({}: FormValues) {
  //   const obj: FormErrors = {}

  //   return obj
  // }

  const onSubmit = useCallback(async (
    {name}: FormValues,
    {setFieldError}: FormikHelpers<FormValues>,
  ) => {
    const nameParts = name.split(/\s+/)
    const lastName = nameParts.length > 1 ? nameParts.pop() || '' : ''
    const firstName = nameParts.join(' ')

    const {data: _data} = await accountUpdate({variables: {
      firstName,
      lastName,
    }})

    if(_data?.accountUpdate?.user) {
      setShowSuccessAlert(true)
    }

    _data?.accountUpdate?.accountErrors.forEach((err) => {
      if (err.field) {
        const msg = 'Ekki Tókst að breyta notandaupplýsingum'
        if (msg == null) {
          // TODO: Log to sentry
          console.error(`Got an undefined error code (${err.code}) for field: ${err.field}`)
        }
        setFieldError(err.field as keyof FormValues, msg ?? `Óþekkt villa (${err.code})`)
      }
    })
  }, [accountUpdate])

  const formikProps: FormikConfig<FormValues> = {
    initialValues: {
      name: user?.name || '',
    },
    onSubmit,
    // validate,
  }

  function CompanyBadge() {
    return (
      <p className="mb-0">
        <span className="badge bg-secondary px-2 py-1">
          <Building size={15} className="mr-2 mb-1 text-white" />
          {company?.name}
        </span>
      </p>
    )
  }

  if (userLoading || ordersIsLoading || requestsIsLoading) {
    return <Loader />
  }

  return (
    <Page>
      <Container size="lg">
        {showSuccessAlert && (
          <div className="alert alert-success alert-dismissible fade show" role="alert">
            Upplýsingar hafa verið uppfærðar!
            <button type="button" className="close" onClick={() => setShowSuccessAlert(false)}>
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
        )}

        <div className="py-5">
          <div className="d-flex mb-5 align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <Gear size={25} className="mr-3" />
              <h2 className="mb-0">Stillingar</h2>
            </div>
            <button className="btn btn-danger" onClick={onClickLogOut}>
              <DoorClosed size={15} className="mr-2 mb-1" />
              Útskrá
            </button>
          </div>

          <div className="p-4 p-lg-5 bg-white rounded border border-gray mb-5">
            <Formik component={UserSettingsForm} {...formikProps} />
          </div>

          {!!requestsData?.length && (
            <>
              <div className="d-flex pt-5 mb-5 align-items-center justify-content-between">
                <h2 className="mb-0">Beiðnir í vinnslu</h2>
              </div>

              <div className="row mb-2">
                {requestsData?.map((req: any, i: number) => (
                  <div key={i} className="col-12 col-md-6 col-xl-4 mb-4">
                    <RequestItem req={req} />
                  </div>
                ))}
              </div>
            </>
          )}

          {!!ordersData?.length && (
            <>
              <div className="d-flex pt-5 mb-5 align-items-center justify-content-between">
                <h2 className="mb-0">Síðustu pantanir</h2>
                <CompanyBadge />
              </div>

              <div className="row mb-2">
                {take(ordersData, 3).map((order: any, index: number) => (
                  <div key={index} className="col-12 col-md-6 col-xl-4 mb-4">
                    <OrderItem order={order} />
                  </div>
                ))}
              </div>
              <div className="d-flex justify-content-end mb-5">
                <Link
                  to="/verslun/pantanir/"
                  className="btn btn-link text-secondary"
                >
                  Sjá allar pantanir
                  <ArrowRight className="ml-2" />
                </Link>
              </div>
            </>
          )}

          {!!company?.addresses?.length && (
            <>
              <div className="d-flex pt-5 mb-5 align-items-center justify-content-between">
                <h2 className="mb-0">Vistuð heimilisföng</h2>
                <CompanyBadge />
              </div>

              <div className="row mb-2 mb-5">
                {company?.addresses?.map((address: any, index: number) => (
                  <div key={index} className="col-12 col-md-6 mb-4">
                    <AddressItem address={address} />
                  </div>
                ))}
              </div>
            </>
          )}

        </div>
      </Container>
    </Page>
  )
}

function UserSettingsForm({
  handleSubmit,
  isSubmitting,
  dirty,
  isValid,
}: FormikProps<FormValues>) {
  // TODO: Add email and phone, or remove both?

  return (
    <form onSubmit={handleSubmit}>
      <div className="row mb-4">
        <div className="col-12 col-md-6 col-xl-4">
          <Field name="name" type="text" label="Nafn" className="mb-3" />
        </div>
        <div className="col-12 col-md-6 col-xl-4">
          <Field name="email" type="text" label="Netfang" className="mb-3" disabled />
        </div>
        <div className="col-12 col-md-6 col-xl-4">
          <Field name="phone" type="text" label="Símanúmer" className="mb-3" disabled />
        </div>
      </div>

      <div className="d-flex justify-content-end">
        <button
          className="btn btn-primary"
          type="submit"
          disabled={isSubmitting || !dirty || !isValid}
        >
          Vista
        </button>
      </div>
    </form>
  )
}
